import { useMemo } from 'react';
import { Megaphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Popup } from '../../models/Popup';
import { PopupListRow } from './PopupListRow';

interface PopupsListProps {
  popups: Popup[];
  onPreview: (popup: Popup) => void;
  onViewEvents: (popup: Popup) => void;
  onArchive: (popup: Popup) => void;
  onRestore: (popup: Popup) => void;
  onDelete: (popup: Popup) => void;
}

export function PopupsList({ popups, onPreview, onViewEvents, onArchive, onRestore, onDelete }: PopupsListProps) {
  const { t } = useTranslation('popups');

  const rowData = useMemo(
    () =>
      popups.map((popup, index) => ({
        popup,
        isLast: index === popups.length - 1,
      })),
    [popups],
  );

  return (
    <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-background">
      <div className="grid shrink-0 grid-cols-[1fr_auto_2.5rem] items-center gap-3 border-b border-border bg-background px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Megaphone className="h-4 w-4 shrink-0 text-icons" />
          <span>{t('list.popup')}</span>
        </div>
        <span className="text-xs font-medium text-muted-foreground sm:text-sm">{t('list.frequency')}</span>
        <span className="sr-only">{t('table.actionsMenu')}</span>
      </div>

      <div className="min-h-0 overflow-y-auto overscroll-contain pb-px">
        {rowData.map(({ popup, isLast }) => (
          <PopupListRow
            key={popup.id}
            popup={popup}
            isLast={isLast}
            onPreview={onPreview}
            onViewEvents={onViewEvents}
            onArchive={onArchive}
            onRestore={onRestore}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}
